import { sellerProductCategoryLabel } from "../products/seller-product-mapper";

/** Map seller order API payloads to shapes used by the orders list + detail UI. */

const STEP_INDEX_BY_UI_STATUS = {
  new_order: 0,
  processing: 1,
  ready_pickup: 2,
  out_delivery: 3,
  delivered: 4,
  failed_delivery: 3,
  cancelled: 0,
};

const STEP_LABELS = [
  "Order Placed",
  "Processing",
  "Ready for Pick up",
  "Out for Delivery",
  "Delivered",
];

function formatOrderDateLong(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatOrderDateShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function formatMoney(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "$0.00";
  return `$${x.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Map API order line `status` to UI keys used by `OrderStatusBadge` / filters.
 */
export function normalizeOrderUiStatus(apiStatus) {
  const raw = String(apiStatus ?? "").trim();
  if (!raw) return "processing";
  const u = raw.toUpperCase().replace(/\s+/g, "_");
  if (u.includes("CANCEL")) return "cancelled";
  if (u.includes("FAIL")) return "failed_delivery";
  if (u === "DELIVERED" || (u.includes("DELIVER") && !u.includes("OUT")))
    return "delivered";
  if (u.includes("OUT") && u.includes("DELIV")) return "out_delivery";
  if (u.includes("PICKUP") || u.includes("PICK_UP") || u.includes("READY"))
    return "ready_pickup";
  if (
    u.includes("NEW") ||
    u.includes("PLACED") ||
    u.includes("PENDING") ||
    u.includes("CREATED")
  )
    return "new_order";
  if (u.includes("PROCESS")) return "processing";
  return "processing";
}

export function mapSellerOrderItemToRow(item) {
  const id = item?._id != null ? String(item._id) : "";
  const ref = item?.reference != null ? String(item.reference) : id;
  const qty = Number(item?.quantity) || 0;
  const unit = Number(item?.pricePerUnit);
  const subtotal = Number.isFinite(unit) && Number.isFinite(qty) ? unit * qty : 0;
  const displayAmount =
    item?.amountPaid != null
      ? Number(item.amountPaid)
      : item?.sellerAmount != null
        ? Number(item.sellerAmount)
        : subtotal;

  const product = item?.product;
  const categoryLabel = product ? sellerProductCategoryLabel(product) : "—";

  return {
    id,
    orderId: ref || id,
    customerName: "—",
    customerCity: "—",
    productName: product?.name?.trim() || "Order item",
    sku: ref || "—",
    quantity: qty,
    price: formatMoney(displayAmount),
    status: normalizeOrderUiStatus(item?.status),
    category: categoryLabel,
    raw: item,
  };
}

/**
 * Detail page view-model (same fields as `getOrderDetail` mock).
 */
export function mapSellerOrderDetailToPageDetail(item) {
  if (!item?._id) return null;

  const status = normalizeOrderUiStatus(item.status);
  const activeStep = STEP_INDEX_BY_UI_STATUS[status] ?? 0;
  const qty = Number(item.quantity) || 1;
  const unit = Number(item.pricePerUnit);
  const unitSafe = Number.isFinite(unit) ? unit : 0;
  const subtotal = unitSafe * qty;
  const discount = 0;
  const tax = 0;
  const platformPct = 5;
  const platformFee = Math.round(subtotal * (platformPct / 100));
  const total =
    item.amountPaid != null
      ? Number(item.amountPaid)
      : subtotal - discount + tax + platformFee;

  const headline =
    item.amountPaid != null
      ? Number(item.amountPaid)
      : item.sellerAmount != null
        ? Number(item.sellerAmount)
        : total;

  const productLabel =
    item.product?.name != null && String(item.product.name).trim()
      ? String(item.product.name).trim()
      : "Order item";

  return {
    id: String(item._id),
    orderId: String(item.reference ?? item._id),
    customerName: "—",
    customerCity: "—",
    productName: productLabel,
    sku: String(item.reference ?? "—"),
    quantity: qty,
    price: formatMoney(headline),
    status,
    category: "",
    datePlacedLabel: formatOrderDateLong(item.createdAt),
    dateShortLabel: formatOrderDateShort(item.createdAt),
    orderRef: String(item.reference ?? item._id),
    headlineAmount: formatMoney(headline),
    activeStep,
    stepLabels: STEP_LABELS,
    lineItems: [
      {
        id: "1",
        name: productLabel,
        sku: String(item.reference ?? "—"),
        qty,
        unitPrice: formatMoney(unitSafe),
        subtotal: formatMoney(subtotal),
      },
    ],
    payment: {
      subtotal,
      discount,
      tax,
      platformPct,
      platformFee,
      total,
    },
    customer: {
      name: "—",
      location: "—",
      email: "—",
      phone: "—",
      deliveryType: "—",
      address: "—",
    },
    cancellationReason: null,
    raw: item,
  };
}

/**
 * Maps API order line `status` to the next seller action (mirrors
 * `SellOnMamaket/Order/Overview.jsx` timeline: Paid → process, Processing → ship, Shipped → deliver).
 */
export function getLifecycleActionKey(rawStatus) {
  const s = String(rawStatus ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  if (!s) return null;
  if (s.includes("CANCEL") || s.includes("REFUND")) return null;
  if (s === "DELIVERED") return null;
  if (s.includes("REVIEW") && s.includes("RELEASE")) return null;

  if (s === "PAID" || s === "PAYMENT_CONFIRMED" || s.includes("PAYMENT_CONFIRMED")) {
    return "accept";
  }
  if (s === "PROCESSING" || s === "IN_PROCESSING") {
    return "ship";
  }
  if (s === "SHIPPED" || s === "OUT_FOR_DELIVERY" || s.includes("OUT_FOR_DELIVERY")) {
    return "deliver";
  }
  return null;
}

export function getLifecyclePrimaryButtonTitle(actionKey) {
  if (actionKey === "accept") return "Accept Order";
  if (actionKey === "ship") return "Order Shipped";
  if (actionKey === "deliver") return "Order Delivered";
  return "Accept Order";
}
